/**
 * MyAE Dashboard Sidebar Collapse Functionality
 */

class DashboardSidebar {
    private sidebar: HTMLElement | null;
    private main: HTMLElement | null;
    private collapseButton: HTMLButtonElement | null;
    private logoIcon: HTMLElement | null;
    private profileMenuToggle: HTMLButtonElement | null;
    private profileMenu: HTMLElement | null;
    private profileContainer: HTMLElement | null;
    private isCollapsed: boolean = false;
    private isProfileMenuOpen: boolean = false;

    // Event handler references for cleanup
    private clickOutsideHandler = (e: Event) => {
        if (this.isProfileMenuOpen && this.profileMenu) {
            const target = e.target as HTMLElement;
            if (!this.profileMenu.contains(target) &&
                target !== this.profileMenuToggle &&
                !this.profileContainer?.contains(target)) {
                this.closeProfileMenu();
            }
        }
    };

    private collapseButtonHandler = () => {
        this.collapse();
    };

    private logoIconHandler = () => {
        if (this.isCollapsed) {
            this.expand();
        } else {
            this.collapse();
        }
    };

    private profileMenuToggleHandler = (e: Event) => {
        e.stopPropagation();
        this.toggleProfileMenu();
    };

    private profileContainerHandler = (e: Event) => {
        if (this.isCollapsed) {
            e.stopPropagation();
            this.toggleProfileMenu();
        }
    };

    constructor() {
        this.sidebar = document.getElementById('myae-sidebar');
        this.main = document.getElementById('myae-main');
        this.collapseButton = document.getElementById('myae-sidebar-collapse') as HTMLButtonElement;
        this.logoIcon = document.getElementById('myae-logo-icon');
        this.profileMenuToggle = document.getElementById('profile-menu-toggle') as HTMLButtonElement;
        this.profileMenu = document.getElementById('profile-menu');
        this.profileContainer = document.querySelector('.myae-sidebar__profile');

        this.init();
    }

    private init(): void {
        if (!this.sidebar || !this.main || !this.collapseButton || !this.logoIcon) {
            return;
        }

        // Collapse button click handler
        this.collapseButton.addEventListener('click', this.collapseButtonHandler);

        // Logo icon click handler (toggles sidebar)
        this.logoIcon.addEventListener('click', this.logoIconHandler);

        // Profile menu toggle handler
        if (this.profileMenuToggle && this.profileMenu) {
            this.profileMenuToggle.addEventListener('click', this.profileMenuToggleHandler);

            // Profile container click handler (when collapsed, entire container opens menu)
            if (this.profileContainer) {
                this.profileContainer.addEventListener('click', this.profileContainerHandler);
            }

            // Close menu when clicking outside
            document.addEventListener('click', this.clickOutsideHandler);
        }
    }

    public destroy(): void {
        // Remove all event listeners to prevent memory leaks
        if (this.collapseButton) {
            this.collapseButton.removeEventListener('click', this.collapseButtonHandler);
        }

        if (this.logoIcon) {
            this.logoIcon.removeEventListener('click', this.logoIconHandler);
        }

        if (this.profileMenuToggle) {
            this.profileMenuToggle.removeEventListener('click', this.profileMenuToggleHandler);
        }

        if (this.profileContainer) {
            this.profileContainer.removeEventListener('click', this.profileContainerHandler);
        }

        document.removeEventListener('click', this.clickOutsideHandler);
    }

    private toggleProfileMenu(): void {
        if (this.isProfileMenuOpen) {
            this.closeProfileMenu();
        } else {
            this.openProfileMenu();
        }
    }

    private openProfileMenu(): void {
        if (!this.profileMenu || !this.profileMenuToggle) return;

        this.isProfileMenuOpen = true;
        this.profileMenu.classList.remove('myae-sidebar__profile-menu--hidden');
        this.profileMenuToggle.setAttribute('aria-expanded', 'true');
    }

    private closeProfileMenu(): void {
        if (!this.profileMenu || !this.profileMenuToggle) return;

        this.isProfileMenuOpen = false;
        this.profileMenu.classList.add('myae-sidebar__profile-menu--hidden');
        this.profileMenuToggle.setAttribute('aria-expanded', 'false');
    }

    private collapse(): void {
        if (!this.sidebar || !this.main) return;

        this.isCollapsed = true;
        this.sidebar.classList.add('myae-sidebar--collapsed');
        this.main.classList.add('myae-main--sidebar-collapsed');

        // Close profile menu when collapsing
        this.closeProfileMenu();
    }

    private expand(): void {
        if (!this.sidebar || !this.main) return;

        this.isCollapsed = false;
        this.sidebar.classList.remove('myae-sidebar--collapsed');
        this.main.classList.remove('myae-main--sidebar-collapsed');
    }
}

// Initialize sidebar when DOM is ready
if (document.getElementById('myae-dashboard')) {
    new DashboardSidebar();
}

export default DashboardSidebar;
